import type { ReactNode } from "react";

interface SectionProps {
  id: string;
  eyebrow?: string;
  title: string;
  /** Une phrase. Ce que fait le composant, ou quand le choisir — rien de plus. */
  description?: ReactNode;
  children: ReactNode;
}

/**
 * Bloc de documentation avec ancre. `scroll-mt` compense la barre supérieure
 * collante.
 *
 * `content-visibility: auto` a été essayé ici et retiré : les sections ont des
 * hauteurs trop dissemblables (une galerie d'icônes contre trois boutons) pour
 * qu'une `contain-intrinsic-size` unique soit crédible, et un bloc non rendu
 * mesure sa taille estimée. Les ancres du sommaire atterrissaient alors à
 * plusieurs centaines de pixels de leur cible. L'utilitaire reste disponible
 * dans le thème pour les listes homogènes, où il tient ses promesses.
 */
export function Section({ id, eyebrow, title, description, children }: SectionProps) {
  return (
    <section
      id={id}
      className="scroll-mt-20 border-t border-border py-14 first:border-t-0 first:pt-0"
    >
      {eyebrow && (
        <p className="mb-2 text-xs font-medium tracking-wide text-accent-fg uppercase">{eyebrow}</p>
      )}
      <h2 className="text-2xl font-semibold tracking-tight text-fg">{title}</h2>
      {description && <p className="mt-2.5 max-w-2xl text-sm text-fg-muted">{description}</p>}
      <div className="mt-7">{children}</div>
    </section>
  );
}

/** Sous-titre à l'intérieur d'une section. */
export function SubSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mt-9 first:mt-0">
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-xs font-semibold tracking-wide text-fg-muted uppercase">{title}</h3>
        {hint && <p className="text-xs text-fg-muted">{hint}</p>}
      </div>
      {children}
    </div>
  );
}
